import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Button, ButtonGroup, Typography, Theme } from '@mui/material';
import Icon from 'icons/Icon';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { usePlatform } from 'platform/PlatformHooks';
import { Accept, FileRejection, useDropzone } from 'react-dropzone';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { isBase64Image } from './ImageUpload';

type Props = {
  key: string;
  id: string;
  label?: string;
  value: string;
  height?: number;
  width?: number;
  onChange: (value: string) => void;
  isDirty: () => boolean;
  onSave: () => Promise<void>;
  onCancel: () => void;
};
const acceptedFileTypes: Accept = { 'image/*': ['.png', '.jpg', '.jpeg'] };

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflow: 'hidden',
    gap: '16px',
  },
  dropzone: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderWidth: 2,
    borderRadius: 2,
    borderColor: theme.palette.divider,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '&$dropped': {
      borderColor: theme.palette.success.light,
    },
    '&$dragActive': {
      borderColor: theme.palette.success.main,
    },
    width: '100%',
    height: '100%',
  },
  dragActive: {},
  dropped: {},
}));

const ImageUploadWithCropper = ({
  id,
  label,
  height,
  width,
  value,
  onChange,
  isDirty,
  onSave,
  onCancel,
}: Props) => {
  const platform = usePlatform();
  const [uncroppedImg, setUncroppedImg] = useState(value);
  const [croppedImg, setCroppedImg] = useState(value);
  const [isCropping, setIsCropping] = useState(false);
  const [, setImageLoaded] = useState(false);
  const [error, setError] = useState('');
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 80,
    height: 80,
    x: 0,
    y: 0,
  });
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setUncroppedImg(value);
    setCroppedImg(value);
  }, [value]);

  const classes = useStyles();
  const convertToDataURL = useCallback(
    (reader: FileReader) => {
      // convert image file to base64 string
      const src = reader.result;

      if (typeof src === 'string') {
        if (!isBase64Image(src)) {
          platform.showError(
            'Only the png, jpg & jpeg type images are allowed!',
          );
          return;
        }
        onChange(src);
        setUncroppedImg(src);
        setCroppedImg(src);
      }
    },
    [onChange, platform],
  );

  const uploadImage = (e: React.SyntheticEvent<unknown, Event>): void => {
    const { target } = e;
    if (
      target instanceof HTMLInputElement &&
      target.files &&
      target.files.length
    ) {
      const file = target.files[0];
      if (file.size > 4000000) {
        setError(`File "${file.name}" is more than 4 MB!`);
      } else {
        const reader = new FileReader();
        setIsCropping(true);
        reader.addEventListener('load', () => convertToDataURL(reader), false);
        reader.readAsDataURL(file);
      }
    }
  };
  const handleImageLoaded = (e: SyntheticEvent<HTMLImageElement>) => {
    if (e.target instanceof HTMLImageElement) {
      imgRef.current = e.target as HTMLImageElement;
      setImageLoaded(true);
    }
  };

  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
      if (fileRejections.length) {
        if (fileRejections.length > 1) {
          setError('You must upload only a single file!');
        } else {
          const f = fileRejections[0];
          setError(
            `${f.file.name} is rejected! ${f.errors
              .map((e) => e.message)
              .join(', ')}!`,
          );
        }
      } else if (acceptedFiles.length === 1) {
        const f = acceptedFiles[0];
        if (f.size > 4000000) {
          setError(`File "${f.name}" is more than 4 MB!`);
        } else {
          setError('');
          const reader = new FileReader();
          setIsCropping(true);
          reader.addEventListener(
            'load',
            () => convertToDataURL(reader),
            false,
          );
          reader.readAsDataURL(f);
        }
      }
    },
    [convertToDataURL],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple: false,
  });

  const getCroppedImg = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const image = imgRef.current;
    if (!(image && crop && ctx)) {
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const pixelRatio = 1; // window.devicePixelRatio;

    crop.width =
      crop.unit === '%' ? ((crop.width ?? 64) / 100) * image.width : crop.width;
    crop.height =
      crop.unit === '%'
        ? ((crop.height ?? 64) / 100) * image.height
        : crop.height;

    canvas.width = (crop.width ?? 64) * pixelRatio;
    canvas.height = (crop.height ?? 64) * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      (crop.x ?? 0) * scaleX,
      (crop.y ?? 0) * scaleY,
      (crop.width ?? 64) * scaleX,
      (crop.height ?? 64) * scaleY,
      0,
      0,
      crop.width ?? 64,
      crop.height ?? 64,
    );
    return canvas.toDataURL();
  };

  const onCrop = () => {
    setIsCropping(true);
  };
  const cropImage = async () => {
    if (isCropping) {
      const croppedImage = await getCroppedImg();
      setCroppedImg(croppedImage as string);
      onChange(croppedImage as string);
      setIsCropping(!isCropping);
    }
  };

  const _onSave = async () => {
    await cropImage();
    await onSave();
  };

  const cancelCrop = (): void => {
    setIsCropping(false);
    setCroppedImg(uncroppedImg);
  };

  const _onCancel = async () => {
    setUncroppedImg(value);
    setCroppedImg(value);
    setIsCropping(false);
    onCancel();
  };

  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <div style={{ marginRight: 8 }}>
        <ButtonGroup
          sx={{ display: 'flex', flexDirection: 'column', border: 'none' }}
        >
          <Button style={{ border: 'none' }}>
            <label
              style={{
                padding: '8px',
                cursor: 'pointer',
              }}
              htmlFor={`file-input-${id}`}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Icon iconName="upload" />
                <div>Upload</div>
              </div>
              <input
                id={`file-input-${id}`}
                type="file"
                name="name"
                style={{ display: 'none' }}
                accept={'image/x-png,image/jpeg,image/png,image/jpg'}
                onChange={uploadImage}
              />
            </label>
          </Button>
          <Button
            onClick={onCrop}
            style={{ border: 'none' }}
            disabled={!uncroppedImg || isCropping}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Icon iconName="crop-alt" />
              <div>Crop</div>
            </div>
          </Button>
          <Button
            onClick={cancelCrop}
            style={{ border: 'none' }}
            disabled={!(uncroppedImg && isCropping)}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Icon iconName="crop" />
              <div>Cancel Crop</div>
            </div>
          </Button>
          <Button
            onClick={_onSave}
            style={{ border: 'none' }}
            disabled={!(isDirty() || isCropping)}
            color="primary"
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Icon iconName="save" />
              <div>Save</div>
            </div>
          </Button>
          <Button
            onClick={_onCancel}
            style={{ border: 'none' }}
            color="primary"
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Icon iconName="times" />
              <div>Cancel</div>
            </div>
          </Button>
        </ButtonGroup>
      </div>
      <div style={{ flexGrow: 1 }}>
        {isCropping ? (
          <div>
            <ReactCrop crop={crop} onChange={(c) => setCrop(c)} aspect={1}>
              <img src={croppedImg} onLoad={handleImageLoaded} />
            </ReactCrop>
          </div>
        ) : (
          <div
            {...getRootProps()}
            className={clsx(classes.dropzone, {
              [classes.dragActive]: isDragActive,
            })}
          >
            <input {...getInputProps()} />
            {isDragActive ? (
              <div style={{ width: '100%' }}>
                <Typography>Drop it now...</Typography>
              </div>
            ) : (
              <div>
                {error ? (
                  <Typography color="error">{error}</Typography>
                ) : (
                  <img
                    src={croppedImg}
                    style={{
                      width: '100%',
                    }}
                    alt=""
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUploadWithCropper;
